'use client';

import React, { ButtonHTMLAttributes } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  type = 'button',
  className = '',
  ...props
}) => {
  const baseClasses = 'font-medium rounded transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 inline-flex items-center justify-center';
  
  const variants: Record<ButtonVariant, string> = {
    primary: 'bg-gradient-to-r from-[#4d8fa7] to-[#508fa8] text-white hover:shadow-lg disabled:opacity-50',
    secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:bg-gray-100',
    outline: 'border-2 border-[#4d8fa7] text-[#4d8fa7] hover:bg-[#4d8fa7] hover:text-white disabled:border-gray-200 disabled:text-gray-400',
    ghost: 'text-[#4d8fa7] hover:bg-[#4d8fa7] hover:bg-opacity-10 disabled:text-gray-400',
  };
  
  const sizes: Record<ButtonSize, string> = {
    small: 'px-4 py-2 text-sm rounded-lg',
    medium: 'px-6 py-3 text-base rounded-xl',
    large: 'px-8 py-4 text-lg rounded-2xl',
  };
  
  const buttonClasses = `${baseClasses} ${variants[variant]} ${sizes[size]} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`;
  
  return (
    <button data-component-id="src/components/ui/Button.tsx:43:4" data-component-path="src/components/ui/Button.tsx" data-component-line="43" data-component-file="Button.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22className%22%3A%22%5Bvar%3AbuttonClasses%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={buttonClasses}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;